/**
 * `GET /api/creation/seller-status` — seller prerequisites before listing creation.
 *
 * @see lib/listing-seller-eligibility.ts
 * @see app/api/creation/seller-status/route.ts
 */

import { z } from "zod";
import { pinned } from "./pin";

export type SellerCheckCode =
  | "CURRENCY_NOT_SET"
  | "ACCOUNT_SUSPENDED"
  | "PHONE_VERIFICATION_REQUIRED"
  | "PAYPAL_NOT_CONNECTED"
  | "NO_SHIPPING_METHOD"
  | "NO_MAIN_ADDRESS";

export const SellerCheckCodeSchema = pinned<SellerCheckCode>()(
  z.enum([
    "CURRENCY_NOT_SET",
    "ACCOUNT_SUSPENDED",
    "PHONE_VERIFICATION_REQUIRED",
    "PAYPAL_NOT_CONNECTED",
    "NO_SHIPPING_METHOD",
    "NO_MAIN_ADDRESS",
  ]),
);

export interface SellerSetupEligibleResponse {
  eligible: true;
}

export interface SellerSetupIneligibleResponse {
  eligible: false;
  blockers: SellerCheckCode[];
  code: SellerCheckCode;
  reason: string;
}

export type SellerSetupResponse = SellerSetupEligibleResponse | SellerSetupIneligibleResponse;

export const SellerSetupEligibleResponseSchema = pinned<SellerSetupEligibleResponse>()(
  z.object({
    eligible: z.literal(true),
  }),
);

export const SellerSetupIneligibleResponseSchema = pinned<SellerSetupIneligibleResponse>()(
  z.object({
    eligible: z.literal(false),
    blockers: z.array(SellerCheckCodeSchema).min(1),
    code: SellerCheckCodeSchema,
    reason: z.string(),
  }),
);

export const SellerSetupResponseSchema = pinned<SellerSetupResponse>()(
  z.discriminatedUnion("eligible", [
    SellerSetupEligibleResponseSchema,
    SellerSetupIneligibleResponseSchema,
  ]),
);
